import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { IconArrowRight } from '@tabler/icons-react';
import { RelatedScreen, Screen } from '../types';
import { apiService } from '../services/api';
import { expandColumns } from '../utils/columnExpander';
import { EditableTableCell } from './EditableTableCell';
import { resolveAppearance } from '../utils/uiUtils';
import { breadcrumbStack, BreadcrumbEntry } from '../utils/breadcrumbStack';
import { useFilters } from '../contexts';
import { resolvePickerRequestFilters } from '../utils/pickerFilters';

interface M2ORecordTableProps {
    /** The screen whose columns describe the record. */
    screen: Screen | RelatedScreen;
    recordId: string | number;
    /** The record itself when the form already holds it; it is shown as is and never requested. */
    record?: any;
    breadcrumbEntry?: BreadcrumbEntry;
    /** Values and screen of the open form: the record is requested with the picker's own parameters. */
    formValues?: Record<string, any>;
    formScreen?: any;
}

export const M2ORecordTable: React.FC<M2ORecordTableProps> = ({ screen, recordId, record, breadcrumbEntry, formValues, formScreen }) => {
    const navigate = useNavigate();
    const { appId } = useParams<{ appId: string }>();
    const { setFilter, getFilter } = useFilters();
    const requestFilters = resolvePickerRequestFilters(screen, formScreen, formValues, getFilter);

    const { data: requested, isLoading, isError } = useQuery({
        queryKey: ['screenRecord', screen.id, recordId, JSON.stringify(requestFilters)],
        queryFn: async () => {
            const response = await apiService.getScreenRecord(screen.id, recordId, requestFilters, false);
            if (!response.success) {
                throw new Error(response.message || 'Nepodarilo sa načítať záznam');
            }
            const payload: any = response.data;
            return payload?.data ?? payload;
        },
        enabled: !record && !!screen.id && !!recordId,
        staleTime: 30_000,
    });
    const data = record ?? requested;

    if (!record && isLoading) {
        return (
            <div className="text-center py-3">
                <Spinner animation="border" size="sm" />
            </div>
        );
    }

    if ((!record && isError) || !data) {
        return <div className="text-danger p-2">Nepodarilo sa načítať záznam.</div>;
    }

    const expanded = expandColumns((screen.columns || []).filter(c => !c.screenHidden));

    const updateForm = (screen as any).updateForms?.[0];
    const hasOneToMany = updateForm?.fields?.some((f: any) => f.type === 'one-to-many');
    const canNavigate = !!appId && data?.id !== undefined;

    const setRowContextFilters = (row: any) => {
        if ((screen as any).contextCode && row.id) {
            const idValue = typeof row.id === 'object' && row.id !== null && row.id.id !== undefined
                ? String(row.id.id)
                : String(row.id);
            setFilter((screen as any).contextCode, idValue);
        }
        (screen.columns || []).forEach((col: any) => {
            if (col.contextCode && row[col.code] !== undefined && row[col.code] !== null) {
                const value = row[col.code];
                const filterValue = typeof value === 'object' && value !== null && value.id !== undefined
                    ? String(value.id)
                    : String(value);
                setFilter(col.contextCode, filterValue);
            }
        });
    };

    const handleNavigate = () => {
        if (!canNavigate) return;
        setRowContextFilters(data);

        const dest = updateForm
            ? (hasOneToMany
                ? `/app/${appId}/update-form/${updateForm.id}/${data.id}`
                : `/app/${appId}/update-simple-form/${updateForm.id}/${data.id}`)
            : `/app/${appId}/screen/${screen.id}`;

        if (breadcrumbEntry) {
            breadcrumbStack.push([breadcrumbEntry], dest);
            breadcrumbStack.markDrilldown();
        }

        if (updateForm) {
            navigate(dest, { state: { rowData: data } });
        } else {
            navigate(dest);
        }
    };

    const noop = () => {};
    const rowUi = resolveAppearance((data as any)?._appearance?.row, 'rule');

    return (
        <div className="table-responsive">
            <table className="table table-vcenter card-table mb-0">
                <thead>
                    <tr>
                        {expanded.map(col => {
                            const headerUi = resolveAppearance(col.appearance, 'column');
                            return (
                                <th
                                    key={col.id + col.dataPath.join('.')}
                                    className={headerUi.headerClassName || undefined}
                                    style={headerUi.headerStyle}
                                >
                                    {col.displayName}
                                </th>
                            );
                        })}
                        {canNavigate && <th style={{ width: '1%' }}></th>}
                    </tr>
                </thead>
                <tbody>
                    <tr
                        onClick={canNavigate ? handleNavigate : undefined}
                        style={{ ...rowUi.style, ...(canNavigate ? { cursor: 'pointer' } : {}) }}
                        className={[canNavigate && 'table-row-clickable', rowUi.className].filter(Boolean).join(' ') || undefined}
                    >
                        {expanded.map(col => (
                            <EditableTableCell
                                key={col.id + col.dataPath.join('.')}
                                row={data}
                                // A preview of the picked record, never an editor: an
                                // editable column would render an inert input and its
                                // cell would swallow the click that opens the record.
                                column={{ ...col, editable: false }}
                                isEditing={false}
                                editValue={null}
                                onEditValueChange={noop}
                                onCellClick={noop}
                                onCellBlur={noop}
                            />
                        ))}
                        {canNavigate && (
                            <td style={{ width: '1%' }}>
                                <div className="d-flex gap-1 justify-content-end align-items-center">
                                    <button
                                        type="button"
                                        className="v5-iconact is-primary"
                                        title="Detail"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleNavigate();
                                        }}
                                    >
                                        <IconArrowRight size={14} strokeWidth={1.75} />
                                    </button>
                                </div>
                            </td>
                        )}
                    </tr>
                </tbody>
            </table>
        </div>
    );
};
